/**
 * The graduated activation pricing schedule (VRG-375).
 *
 * ONE definition, shared by the API that syncs it out of Stripe and the clients
 * that render it. Before this, the schedule existed twice: on the Stripe metered
 * price (what a brand is actually billed) and as a hand-typed array in the
 * platform frontend. Nothing kept the two equal, so editing pricing in Stripe
 * left the brand-facing page advertising the old numbers with no error, no test
 * failure and no signal of any kind — the page simply lied about the price.
 *
 * The schedule belongs to the METERED PRICE, not to the plan. All four plans
 * currently share one metered price, so all four carry the same schedule; a plan
 * given its own metered price would legitimately carry a different one. That is
 * why this rides on the plan row rather than being a global constant — see
 * `activationTiersAgree` for the consequence.
 */
export interface ActivationTier {
  /**
   * Upper bound of the band, inclusive.
   *
   * `null` — NOT `Infinity` — for the final unbounded band. This is deliberate
   * and load-bearing: `JSON.stringify(Infinity)` emits `null`, so `Infinity`
   * cannot survive the wire regardless of what the type claims. Encoding it as
   * `null` up front means the type describes what actually arrives, instead of
   * promising a number that silently becomes null in transit. Stripe uses the
   * same convention (`up_to: null` on the last tier).
   */
  upTo: number | null;
  /** Marginal price per activation, in DOLLARS, for activations inside this band. */
  rate: number;
}

/** Where a given monthly activation count lands in a schedule. */
export interface ResolvedActivationTier {
  lowerBound: number;
  /** `null` when the count falls in the final unbounded band. */
  upperBound: number | null;
  currentRate: number;
  /** The next, cheaper band's rate — `null` when already in the final band. */
  nextRate: number | null;
  /** Activations remaining until the next band. `0` in the final band. */
  toNextTier: number;
}

/**
 * True when the schedule is usable. An empty or absent list means the sync could
 * not resolve tiers out of Stripe, and callers must render NOTHING rather than
 * substitute a default (VRG-375 AC 4) — a wrong price is worse than no price.
 */
export const hasActivationTiers = (
  tiers: ActivationTier[] | null | undefined,
): tiers is ActivationTier[] => Array.isArray(tiers) && tiers.length > 0;

/**
 * Resolve where `count` sits in `tiers`.
 *
 * Returns `null` when the schedule is unusable, so a caller cannot accidentally
 * render a band computed from nothing. Callers must handle null; that is the
 * point.
 */
export const resolveActivationTier = (
  count: number,
  tiers: ActivationTier[] | null | undefined,
): ResolvedActivationTier | null => {
  if (!hasActivationTiers(tiers)) return null;

  // `upTo: null` is the unbounded final band, so it matches any count. Written as
  // an explicit null check rather than treating null as 0, which would make the
  // last band match nothing and push every large count off the end of the list.
  const index = tiers.findIndex(tier => tier.upTo === null || count < tier.upTo);
  const currentIndex = index === -1 ? tiers.length - 1 : index;
  const current = tiers[currentIndex];
  const previous = currentIndex === 0 ? null : tiers[currentIndex - 1];
  const next = tiers[currentIndex + 1] ?? null;

  return {
    lowerBound: previous?.upTo ?? 0,
    upperBound: current.upTo,
    currentRate: current.rate,
    nextRate: next?.rate ?? null,
    toNextTier: current.upTo === null ? 0 : Math.max(current.upTo - count, 0),
  };
};

/**
 * Total list value ($) of `count` activations priced through the schedule.
 *
 * GRADUATED, not volume: each band charges only for the activations inside it,
 * so a month is billed as a blend rather than every activation at the landing
 * band's rate. Returns `null` when the schedule is unusable (see above).
 *
 * No included-allotment discount is applied — this is the gross list value of
 * the activations, which is what the "cost equivalent" tile means.
 */
export const graduatedActivationValue = (
  count: number,
  tiers: ActivationTier[] | null | undefined,
): number | null => {
  if (!hasActivationTiers(tiers)) return null;

  let remaining = count;
  let previousBound = 0;
  let total = 0;

  for (const tier of tiers) {
    if (remaining <= 0) break;
    // The final band is unbounded, so it absorbs everything left.
    const bandSize = tier.upTo === null ? remaining : tier.upTo - previousBound;
    const inBand = Math.min(remaining, bandSize);
    total += inBand * tier.rate;
    remaining -= inBand;
    previousBound = tier.upTo ?? previousBound;
  }

  return total;
};

/**
 * The entry rate — the first band's per-activation price. A plan's included
 * allotment is credited at THIS rate, not at the customer's marginal rate: see
 * `activationCharge`, and `gtm/pricing.md` §4 ("Included = base ÷ entry rate (at
 * $25: $99->4, $299->12, $999->40) so the base is self-funding").
 *
 * Takes tiers rather than reading a constant (VRG-375): this arrived on `main` as
 * `ACTIVATION_TIERS[0].rate` off the hardcoded schedule, and the schedule now comes
 * from Stripe. Null when unresolved, like everything else here.
 */
export const activationEntryRate = (
  tiers: ActivationTier[] | null | undefined,
): number | null => (hasActivationTiers(tiers) ? tiers[0].rate : null);

/**
 * What a period's activations COST, and what the plan's allotment SAVED.
 *
 * THE RULE (`gtm/pricing.md` §6): `base + graduated usage on (activations -
 * included)` — which the §6 revenue table disambiguates as the full graduated
 * ladder priced on the TOTAL activation count, less the included allotment
 * credited at the ENTRY rate. Verified against the published rows, and those
 * checks are now TESTS rather than a comment (`activation-tiers.guard.spec.ts`):
 *
 *   Business, 200 act/mo, 12 incl:   $299 + ladder(200)   - 12 x $25 = $4,499
 *   Enterprise, 10k act/mo, 40 incl: $999 + ladder(10000) - 40 x $25 = $111,499
 *
 * The tempting alternative — `ladder(activations - included)`, anchoring the ladder
 * at the BILLABLE count — matches only 2 of the 6 published rows. It agrees below
 * the first band boundary (which is why it survives casual checking) and then
 * OVERCHARGES: $4,559 against a published $4,499, $112,139 against $111,499. Do not
 * reintroduce it; the guard spec asserts against it by name.
 *
 * The two figures sum to `graduatedActivationValue(count)` by construction, so the
 * "cost" and "covered by your plan" tiles can never disagree.
 *
 * ⚠️ THE LIVE BILLING IMPLEMENTATION DOES NOT DO THIS
 * (VRG-392-Billing.Activation.Charge.Diverges.From.Canon). An allotment-covered
 * activation is marked `included` and never sent to Stripe
 * (`certificate-metering.use-case.ts`), and the Stripe price carries no free tier —
 * so Stripe prices `ladder(billable)`, the rejected formula, and overcharges past
 * 100 activations. This function is the CANON; where it and an actual Stripe
 * invoice disagree, the invoice is the bug.
 */
export const activationCharge = (
  count: number,
  includedActivations: number,
  tiers: ActivationTier[] | null | undefined,
): {cost: number; covered: number} | null => {
  const entryRate = activationEntryRate(tiers);
  const gross = graduatedActivationValue(count, tiers);
  if (entryRate === null || gross === null) return null;

  // Capped at what was actually used: consuming 2 of 12 included saves the value
  // of 2, not of 12. Also what keeps `cost` from going negative under the allotment.
  const includedUsed = Math.min(count, Math.max(includedActivations, 0));
  const covered = includedUsed * entryRate;
  return {cost: gross - covered, covered};
};

/**
 * Whether every plan carries the same schedule.
 *
 * The UI says "Activation pricing. The same on every plan." That was true by
 * assumption while the schedule was a single hardcoded constant — it could not
 * have been otherwise. Now that each plan carries the tiers of whichever metered
 * price it actually uses, the claim is CHECKABLE, and a plan given its own
 * metered price would make it false. Callers should not print that sentence
 * unless this returns true.
 */
export const activationTiersAgree = (
  schedules: (ActivationTier[] | null | undefined)[],
): boolean => {
  const usable = schedules.filter(hasActivationTiers);
  if (usable.length === 0) return false;
  const [first, ...rest] = usable;
  const key = (tiers: ActivationTier[]) =>
    tiers.map(t => `${t.upTo ?? '*'}:${t.rate}`).join('|');
  const reference = key(first);
  return rest.every(tiers => key(tiers) === reference);
};
